/**
 * @fileoverview POST handler for the "/engagement/email-message/dnd-editor/simple-to-full-json"
 * endpoint. Proxies a Beefree "Simple Schema" document to Content Services and returns the full
 * Beefree JSON; the api_key (Bearer) stays server-side.
 */
import fetch from "@lib/fetch";

import { reactQueryMutationAdapter } from "../reactQueryFetcherAdapters";
import { postEngagementEmailMessageDndEditorSimpleToFullJsonCacheKeys } from "./constants";
import type {
  PostEngagementEmailMessageDndEditorSimpleToFullJsonProps,
  PostEngagementEmailMessageDndEditorSimpleToFullJsonResult,
} from "./types";

const simpleToFullJsonUrl = "/engagement/email-message/dnd-editor/simple-to-full-json";

/**
 * Deep-removes `null`/`undefined` object properties. The Simple Schema is OpenAI-strict (every field
 * present, unused ones `null`), but Beefree's converter expects sparse objects — so strip the nulls
 * first. Array items are recursed into but never dropped; the schema has no nullable array items
 * (every array wraps a required object), so no null ever sits at an array position.
 */
const stripNulls = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.map(stripNulls);
  }
  if (value !== null && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>)
        .filter(([, v]) => v !== null && v !== undefined)
        .map(([k, v]) => [k, stripNulls(v)])
    );
  }
  return value;
};

export const postEngagementEmailMessageDndEditorSimpleToFullJson = reactQueryMutationAdapter(
  postEngagementEmailMessageDndEditorSimpleToFullJsonCacheKeys,
  (
    props: PostEngagementEmailMessageDndEditorSimpleToFullJsonProps
  ): Promise<PostEngagementEmailMessageDndEditorSimpleToFullJsonResult> => {
    // `stripNulls(props)` is passed as an expression (not an object literal) so the sparse Beefree
    // payload reaches the server with its own keys intact — never rewritten by the snake_case lint fix.
    return fetch(simpleToFullJsonUrl, "POST", stripNulls(props) as Record<string, unknown>, undefined, {
      // The tool surfaces failure through Operator's own "failed" state; don't also pop a global modal.
      propagateFetchRequestError: false,
    });
  }
);
